//Force 1
import React, { Component } from 'react';
import { BrowserRouter, Switch, Route, Redirect } from 'react-router-dom';
import { Auth } from 'aws-amplify';
import { connect } from 'react-redux';
import './App.css';

import { configureAxios, configureAxiosForE2ETest, getE2ETestSessionEmail, isE2ETest } from './Auth';

import Analytics from './components/Analytics/Analytics';
import Announcements from './components/Announcements/Announcements';
import Brands from './components/Brands/Brands';
import BrandReports from './components/BrandReports/BrandReports';
import Chat from './components/Chat/Chat';
import Consults from './components/Consults/Consults';
import Login from './components/Login/Login';
import Pins from './components/Pins/Pins';
import Products from './components/Products/Products';
import Catalogs from './components/Catalogs/Catalogs';
import Hierarchy from './components/Hierarchy/Hierarchy';
import MainContainer from './components/MainContainer/MainContainer';
import Newsletters from './components/Newsletters/Newsletters';
import NewProducts from './components/NewProducts/NewProducts';
import Scrapers from './components/Scrapers/Scrapers';
import Contracts from './components/Contracts/Contracts';
import ContractTaskTemplates from './components/Contracts/ContractTaskTemplates';
import Subscriptions from './components/Subscriptions/Subscriptions';
import Users from './components/Users/Users';
import Waitlist from './components/Waitlist/Waitlist';
import BrandOnboarding from './components/BrandOnboarding/BrandOnboarding';
import Merchants from './components/Merchants/Merchants';
import MerchantsV2 from './components/MerchantsV2/MerchantsV2';
import MerchantCommissionDisclaimers from './components/MerchantCommissionDisclaimers/MerchantCommissionDisclaimers';
import Invoices from './components/Invoices/Invoices';
import GrowthScraper from './components/GrowthScraper/GrowthScraper';
import ML from './components/ML/ML';
import Concierge from './components/Concierge/Concierge';
import CreatorFeedback from './components/CreatorFeedback/CreatorFeedback';
import MlTraining from './components/MlTraining/MlTraining';
import Prompts from './components/Prompts/Prompts';
import LookbookOrders from './components/LookbookOrders/LookbookOrders';
import Greenhouse from './components/Greenhouse/Greenhouse';
import AllBrands from './components/AllBrands/AllBrands';
import UrlData from './components/UrlData/UrlData';
import LinksToCheck from './components/LinksToCheck/LinksToCheck';
import BulkManage from './components/UrlData/BulkManage';
import CatalogTool from './components/CatalogTool/CatalogTool';
import UrlDataExplore from './components/UrlData/UrlDataExplore';
import UrlDataReports from './components/UrlData/UrlDataReports';
import ProductSinkDetach from './components/UrlData/ProductSinkDetach';
import Logout from './components/Redirects/Logout';
import { logoutUser, syncCurrentUser } from './actions/userActions';
import { EditProduct } from './components/Products/EditProduct';
import UrlSiteRules from './components/UrlSiteRules/UrlSiteRules';
import { ADMIN_SCOPES } from './constants/admin_scope_constants';
import { hasScope } from './helpers/scope_helpers';
import Playground from './components/Playground/Playground';
import Noir from './components/Noir/Noir';
import Discover from './components/Discover/Discover';
import Circles from './components/Circles/Circles';
import Notifications from './components/Notifications/Notifications';
import ShopMyDM from './components/ShopMyDM/ShopMyDM';
import StyleAudit from './components/StyleAudit/StyleAudit';
import { Grids } from './components/Recommendations/Grids';
import { EditGrid } from './components/Recommendations/EditGrid';
import ManualOrderAdjustments from './components/ManualOrderAdjustments/ManualOrderAdjustments';
import Office from './components/Office/Office';
import Changelog from './components/Changelog/Changelog';
import Webinars from './components/Webinars/Webinars';
import PinMatchSilentEval from './components/PinMatchSilentEval/PinMatchSilentEval';
import OAuth from './components/OAuth/OAuth';
import Spotlights from './components/Spotlights/Spotlights';
import BrandBilling from './components/BrandBilling/BrandBilling';
import Moments from './components/Moments/Moments';
import CollectionAnalysis from './components/CollectionAnalysis/CollectionAnalysis';
import BlendSearch from './components/BlendSearch/BlendSearch';
import UserTipDefinitions from './components/UserTips/UserTipDefinitions';
import Wheel from './components/Wheel/Wheel';
import ProductMentions from './components/ProductMentions/ProductMentions';
import ScriptRunner from './components/ScriptRunner/ScriptRunner';
import PrivateOffers from './components/PrivateOffers/PrivateOffers';
import SalesAndPromotions from './components/SalesAndPromotions/SalesAndPromotions';
import BrandCancellations from './components/BrandCancellations/BrandCancellations';
import BrandTrackingHealth from './components/BrandTrackingHealth/BrandTrackingHealth';
import ScriptConfig from './components/ScriptConfig/ScriptConfig';

// Priority-ordered: first matching scope determines the default landing page
const DEFAULT_ROUTES = [
  { scope: ADMIN_SCOPES.MANAGE_CATALOG, path: '/products' },
  { scope: ADMIN_SCOPES.MATCH_PRODUCT, path: '/products' },
  { scope: ADMIN_SCOPES.EDIT_PRODUCT, path: '/products' },
  { scope: ADMIN_SCOPES.EDIT_BRAND, path: '/products' },
  { scope: ADMIN_SCOPES.MANAGE_USERS, path: '/users' },
  { scope: ADMIN_SCOPES.MANAGE_ANNOUNCEMENTS, path: '/announcements' },
  { scope: ADMIN_SCOPES.MANAGE_NEWSLETTERS, path: '/newsletters' },
  { scope: ADMIN_SCOPES.MANAGE_USER_TIPS, path: '/user-tips' },
  { scope: ADMIN_SCOPES.MANAGE_GREENHOUSE, path: '/greenhouse' },
  { scope: ADMIN_SCOPES.PIN_MATCH_AGENTS, path: '/pin-match-silent-eval' },
  { scope: ADMIN_SCOPES.MANAGE_OAUTH_APPLICATIONS, path: '/oauth' },
  { scope: ADMIN_SCOPES.MANAGE_SPOTLIGHTS, path: '/spotlights' },
  { scope: ADMIN_SCOPES.MANAGE_WEBINARS, path: '/webinars' },
  { scope: ADMIN_SCOPES.MANAGE_INVOICING, path: '/brand-billing' },
  { scope: ADMIN_SCOPES.MANAGE_BRANDS, path: '/brand-cancellations' }
];

class App extends Component {
  componentDidMount = () => {
    if (isE2ETest) {
      const email = getE2ETestSessionEmail();
      if (!email) {
        this.setState({ isLoggedIn: false, authLoading: false });
        return;
      }
      configureAxiosForE2ETest();
      this.props
        .syncCurrentUser(email)
        .then(currentUser => this.setState({ isLoggedIn: Boolean(currentUser), authLoading: false }))
        .catch(() => this.setState({ isLoggedIn: false, authLoading: false }));
      return;
    }

    Auth.currentSession()
      .then(async res => {
        configureAxios(res);

        const email = res.getIdToken().payload.email;
        const currentUser = await this.props.syncCurrentUser(email);
        if (!currentUser) return; // User was logged out due to missing admin scopes

        this.setState({ isLoggedIn: true, authLoading: false });
      })
      .catch(err => {
        console.log('App Auth:', err);
        this.setState({ isLoggedIn: false, authLoading: false });
      });
  };

  state = {
    authLoading: true,
    isLoggedIn: false
  };

  render() {
    const { currentUser } = this.props;
    const adminScopes = currentUser?.scopes || [];
    const { authLoading, isLoggedIn } = this.state;
    if (authLoading) return null;
    if (isLoggedIn && !adminScopes.length) return null;

    if (!isLoggedIn) {
      return (
        <BrowserRouter>
          <Switch>
            {/* Playground accessible without auth in local dev */}
            {process.env.REACT_APP_NODE_ENV === 'development' && (
              <Route
                path='/playground'
                component={props => (
                  <MainContainer>
                    <Playground {...props} />
                  </MainContainer>
                )}
              />
            )}
            <Route
              path='/'
              exact
              component={() => {
                return (
                  <Login
                    completeLogin={() => {
                      this.setState({ isLoggedIn: true });
                    }}
                  />
                );
              }}
            />
            <Route
              component={() => {
                return <Redirect to={{ pathname: '/' }} />;
              }}
            />
          </Switch>
        </BrowserRouter>
      );
    }

    const PublicRoute = ({ component: Component, ...rest }) => (
      <Route
        {...rest}
        render={props => (
          <MainContainer>
            <Component {...props} />
          </MainContainer>
        )}
      />
    );

    // When accessed via office.shopmy.us, render only the Office UI (login still required)
    if (window.__IS_OFFICE_DOMAIN__) {
      return (
        <BrowserRouter>
          <Switch>
            <PublicRoute
              path='/logout'
              exact
              component={() => (
                <Logout
                  completeLogout={() => {
                    this.setState({ isLoggedIn: false });
                  }}
                />
              )}
            />
            {hasScope(adminScopes, ADMIN_SCOPES.OFFICE_ADMIN, ADMIN_SCOPES.OFFICE_EXEC, ADMIN_SCOPES.OFFICE_MANAGER, ADMIN_SCOPES.OFFICE_STANDARD) ? (
              <Route path='/' render={props => <Office {...props} currentUser={currentUser} />} />
            ) : (
              <Route path='/' render={() => <div style={{ padding: 40, textAlign: 'center' }}>You do not have office access.</div>} />
            )}
          </Switch>
        </BrowserRouter>
      );
    }

    return (
      <BrowserRouter>
        <Switch>
          <Route
            path='/'
            exact
            component={() => {
              const defaultRoute = DEFAULT_ROUTES.find(r => hasScope(adminScopes, r.scope));
              return <Redirect to={{ pathname: defaultRoute?.path || '/office' }} />;
            }}
          />
          <PublicRoute
            path='/logout'
            exact
            component={() => (
              <Logout
                completeLogout={() => {
                  this.setState({ isLoggedIn: false });
                }}
              />
            )}
          />

          {/* Office — full-screen, bypasses MainContainer */}
          {hasScope(adminScopes, ADMIN_SCOPES.OFFICE_ADMIN, ADMIN_SCOPES.OFFICE_EXEC, ADMIN_SCOPES.OFFICE_MANAGER, ADMIN_SCOPES.OFFICE_STANDARD) && (
            <Route path='/office' render={props => <Office {...props} currentUser={currentUser} />} />
          )}

          {/* Catalog Management */}
          {hasScope(adminScopes, ADMIN_SCOPES.MANAGE_CATALOG) && (
            <PublicRoute path='/catalogs/:brandKey?/:integrationKey?/:sectionKey?' component={() => <Catalogs />} />
          )}
          {hasScope(adminScopes, ADMIN_SCOPES.MANAGE_CATALOG) && <PublicRoute path='/hierarchy' component={() => <Hierarchy />} />}
          {hasScope(adminScopes, ADMIN_SCOPES.MANAGE_CATALOG, ADMIN_SCOPES.EDIT_PRODUCT, ADMIN_SCOPES.MATCH_PRODUCT) && (
            <PublicRoute path='/products/:id' component={() => <EditProduct />} />
          )}
          {hasScope(adminScopes, ADMIN_SCOPES.MANAGE_CATALOG, ADMIN_SCOPES.EDIT_PRODUCT, ADMIN_SCOPES.MATCH_PRODUCT) && (
            <PublicRoute path='/products' exact component={() => <Products />} />
          )}
          {hasScope(adminScopes, ADMIN_SCOPES.MANAGE_CATALOG) && <PublicRoute path='/pins' component={() => <Pins />} />}
          {hasScope(adminScopes, ADMIN_SCOPES.MANAGE_CATALOG) && <PublicRoute path='/merchants' component={() => <Merchants />} />}
          {hasScope(adminScopes, ADMIN_SCOPES.MANAGE_CATALOG) && (
            <PublicRoute path='/merchantsV2/:id?' component={() => <MerchantsV2 />} />
          )}
          {hasScope(adminScopes, ADMIN_SCOPES.MANAGE_CATALOG) && (
            <PublicRoute path='/merchant-commission-disclaimers/:id?' component={() => <MerchantCommissionDisclaimers />} />
          )}
          {hasScope(adminScopes, ADMIN_SCOPES.MANAGE_CATALOG) && <PublicRoute path='/product-mentions' component={() => <ProductMentions />} />}
          {hasScope(adminScopes, ADMIN_SCOPES.MANAGE_CATALOG) && <PublicRoute path='/url-site-rules' component={() => <UrlSiteRules />} />}
          {hasScope(adminScopes, ADMIN_SCOPES.MANAGE_CATALOG) && <PublicRoute path='/catalog-tool' exact component={() => <CatalogTool />} />}
          {hasScope(adminScopes, ADMIN_SCOPES.MANAGE_CATALOG) && <PublicRoute path='/catalog-tool/bulk-manage' component={() => <BulkManage />} />}
          {hasScope(adminScopes, ADMIN_SCOPES.MANAGE_CATALOG) && <PublicRoute path='/catalog-tool/explore' component={() => <UrlDataExplore />} />}
          {hasScope(adminScopes, ADMIN_SCOPES.MANAGE_CATALOG) && (
            <PublicRoute path='/catalog-tool/product-sinks' component={() => <ProductSinkDetach />} />
          )}
          {hasScope(adminScopes, ADMIN_SCOPES.MANAGE_CATALOG) && <PublicRoute path='/catalog-tool/reports' component={() => <UrlDataReports />} />}
          {/* These tabs used to live under /url-data; keep shared links working */}
          {hasScope(adminScopes, ADMIN_SCOPES.MANAGE_CATALOG) && (
            <Route path='/url-data/bulk' render={({ location }) => <Redirect to={{ ...location, pathname: '/catalog-tool/bulk-manage' }} />} />
          )}
          {hasScope(adminScopes, ADMIN_SCOPES.MANAGE_CATALOG) && (
            <Route path='/url-data/explore' render={({ location }) => <Redirect to={{ ...location, pathname: '/catalog-tool/explore' }} />} />
          )}
          {hasScope(adminScopes, ADMIN_SCOPES.MANAGE_CATALOG) && (
            <Route
              path='/url-data/product-sinks'
              render={({ location }) => <Redirect to={{ ...location, pathname: '/catalog-tool/product-sinks' }} />}
            />
          )}
          {hasScope(adminScopes, ADMIN_SCOPES.MANAGE_CATALOG) && (
            <Route path='/url-data/reports' render={({ location }) => <Redirect to={{ ...location, pathname: '/catalog-tool/reports' }} />} />
          )}
          {/* Matchers could open Reports before it moved; send their old links back to Match instead of a blank page */}
          {hasScope(adminScopes, ADMIN_SCOPES.MATCH_PRODUCT) && <Route path='/url-data/reports' render={() => <Redirect to='/url-data' />} />}
          {hasScope(adminScopes, ADMIN_SCOPES.MANAGE_CATALOG) && <PublicRoute path='/scrapers' component={() => <Scrapers />} />}

          {hasScope(adminScopes, ADMIN_SCOPES.MANAGE_USERS, ADMIN_SCOPES.MANAGE_BRANDS) && <PublicRoute path='/private-offers' component={() => <PrivateOffers />} />}
          {/* Support / User Management */}
          {hasScope(adminScopes, ADMIN_SCOPES.MANAGE_USERS) && <PublicRoute path='/brands' component={() => <Brands />} />}
          {hasScope(adminScopes, ADMIN_SCOPES.MANAGE_USERS) && <PublicRoute path='/brandreports' component={() => <BrandReports />} />}
          {hasScope(adminScopes, ADMIN_SCOPES.MANAGE_INVOICING) && <PublicRoute path='/brand-billing' component={() => <BrandBilling />} />}
          {hasScope(adminScopes, ADMIN_SCOPES.MANAGE_USERS, ADMIN_SCOPES.MANAGE_BRANDS) && (
            <PublicRoute path='/brand-cancellations' component={() => <BrandCancellations />} />
          )}
          {hasScope(adminScopes, ADMIN_SCOPES.EDIT_BRAND) && <PublicRoute path='/brand-tracking-health' component={() => <BrandTrackingHealth />} />}
          {hasScope(adminScopes, ADMIN_SCOPES.MANAGE_USERS) && <PublicRoute path='/users' component={() => <Users />} />}
          {hasScope(adminScopes, ADMIN_SCOPES.MANAGE_USERS) && <PublicRoute path='/shopmy-dm' component={() => <ShopMyDM />} />}
          {hasScope(adminScopes, ADMIN_SCOPES.MANAGE_USERS) && <PublicRoute path='/waitlist' component={() => <Waitlist />} />}
          {hasScope(adminScopes, ADMIN_SCOPES.MANAGE_USERS) && <PublicRoute path='/brand-onboarding' component={() => <BrandOnboarding />} />}
          {hasScope(adminScopes, ADMIN_SCOPES.MANAGE_USERS) && <PublicRoute path='/invoices' component={() => <Invoices />} />}
          {hasScope(adminScopes, ADMIN_SCOPES.MANAGE_USERS) && <PublicRoute path='/subscriptions' component={() => <Subscriptions />} />}

          {/* Spotlights dashboard */}
          {hasScope(adminScopes, ADMIN_SCOPES.MANAGE_SPOTLIGHTS) && <PublicRoute path='/spotlights' component={() => <Spotlights />} />}

          {/* Matchers */}
          {hasScope(adminScopes, ADMIN_SCOPES.EDIT_BRAND) && <PublicRoute path='/all-brands' component={() => <AllBrands />} />}
          {hasScope(adminScopes, ADMIN_SCOPES.MATCH_PRODUCT, ADMIN_SCOPES.MANAGE_CATALOG) && (
            <PublicRoute path='/url-data' exact component={() => <UrlData />} />
          )}
          {hasScope(adminScopes, ADMIN_SCOPES.REVIEW_HELD_LINKS) && <PublicRoute path='/links-to-check' component={() => <LinksToCheck />} />}

          {/* Marketing */}
          {hasScope(adminScopes, ADMIN_SCOPES.MANAGE_ANNOUNCEMENTS) && <PublicRoute path='/announcements' component={() => <Announcements />} />}
          {hasScope(adminScopes, ADMIN_SCOPES.MANAGE_NEWSLETTERS) && <PublicRoute path='/newsletters' component={() => <Newsletters />} />}
          {hasScope(adminScopes, ADMIN_SCOPES.MANAGE_NEWSLETTERS, ADMIN_SCOPES.MANAGE_MARKETING, ADMIN_SCOPES.MANAGE_CATALOG) && (
            <PublicRoute path='/sales' component={() => <SalesAndPromotions />} />
          )}
          {hasScope(adminScopes, ADMIN_SCOPES.MANAGE_PRODUCT_DROPS) && <PublicRoute path='/new-products' component={() => <NewProducts />} />}
          {hasScope(adminScopes, ADMIN_SCOPES.MANAGE_USER_TIPS) && <PublicRoute path='/user-tips' component={() => <UserTipDefinitions />} />}
          {hasScope(adminScopes, ADMIN_SCOPES.MANAGE_MARKETING, ADMIN_SCOPES.MANAGE_USERS) && (
            <PublicRoute
              path='/notifications'
              component={() => (
                <Notifications
                  canManageMarketing={hasScope(adminScopes, ADMIN_SCOPES.MANAGE_MARKETING)}
                  canChangeRollout={hasScope(adminScopes, ADMIN_SCOPES.LAUNCH_NOTIFICATIONS)}
                />
              )}
            />
          )}

          {/* Concierge / Noir */}
          {hasScope(adminScopes, ADMIN_SCOPES.MANAGE_USERS) && <PublicRoute path='/concierge' component={() => <Concierge />} />}
          {hasScope(adminScopes, ADMIN_SCOPES.MANAGE_USERS) && <PublicRoute path='/moments/:momentKey?' component={() => <Moments />} />}
          {hasScope(adminScopes, ADMIN_SCOPES.MANAGE_USERS) && <PublicRoute path='/noir' component={() => <Noir />} />}
          {hasScope(adminScopes, ADMIN_SCOPES.MANAGE_USERS) && <PublicRoute path='/discover' component={() => <Discover />} />}

          {/* Talent */}
          {hasScope(adminScopes, ADMIN_SCOPES.MANAGE_GREENHOUSE) && <PublicRoute path='/greenhouse' component={() => <Greenhouse />} />}

          {/* Style Audit */}
          {hasScope(adminScopes, ADMIN_SCOPES.VIEW_STYLE_AUDIT) && <PublicRoute path='/style-audit' component={() => <StyleAudit />} />}

          {/* Order Adjustments */}
          {hasScope(adminScopes, ADMIN_SCOPES.WRITE_MANUAL_ORDER_ADJUSTMENTS) && (
            <PublicRoute path='/manual-order-adjustments' component={() => <ManualOrderAdjustments />} />
          )}

          {/* Changelog — viewable by all admins, editable by MANAGE_CHANGELOG */}
          <PublicRoute path='/changelog' component={() => <Changelog />} />

          {/* Webinars — public webinar hub content management, open to the whole marketing team */}
          {hasScope(adminScopes, ADMIN_SCOPES.MANAGE_WEBINARS, ADMIN_SCOPES.MANAGE_MARKETING) && (
            <PublicRoute path='/webinars' component={() => <Webinars />} />
          )}

          {/* OAuth Applications */}
          {hasScope(adminScopes, ADMIN_SCOPES.MANAGE_OAUTH_APPLICATIONS) && <PublicRoute path='/oauth' component={() => <OAuth />} />}

          {/* NOTE: every child of this <Switch> must be a single Route/PublicRoute with a `path`.
              Do NOT wrap groups of routes in a Fragment ({cond && (<>…</>)}): react-router v5's <Switch>
              treats a Fragment child (no `path`) as a match-all and stops, making every route after it
              unreachable for users who trigger that branch. Gate each route individually instead. */}

          {/* Pin-Match Silent Eval */}
          {hasScope(adminScopes, ADMIN_SCOPES.PIN_MATCH_AGENTS) && (
            <PublicRoute path='/pin-match-silent-eval' exact component={() => <PinMatchSilentEval />} />
          )}

          {hasScope(adminScopes, ADMIN_SCOPES.MANAGE_RECOMMENDATIONS) && <PublicRoute path='/circles' exact component={() => <Circles />} />}

          {/* Recommendations */}
          {hasScope(adminScopes, ADMIN_SCOPES.MANAGE_RECOMMENDATIONS) && (
            <PublicRoute path='/recommendations/:id' exact component={() => <EditGrid />} />
          )}
          {hasScope(adminScopes, ADMIN_SCOPES.MANAGE_RECOMMENDATIONS) && <PublicRoute path='/recommendations' exact component={() => <Grids />} />}
          {hasScope(adminScopes, ADMIN_SCOPES.MANAGE_RECOMMENDATIONS) && <PublicRoute path='/wheel' component={() => <Wheel />} />}
          {hasScope(adminScopes, ADMIN_SCOPES.MANAGE_SHOPPER_SEARCH) && (
            <PublicRoute path='/collection-ranking' component={() => <CollectionAnalysis />} />
          )}
          {hasScope(adminScopes, ADMIN_SCOPES.MANAGE_SHOPPER_SEARCH) && <PublicRoute path='/blend-search' component={() => <BlendSearch />} />}

          {hasScope(adminScopes, ADMIN_SCOPES.RUN_SCRIPTS) && <PublicRoute path='/scripts/:scriptId?/:runId?' component={() => <ScriptRunner />} />}

          {/* superadmin-only tabs (ALL scope) */}
          {adminScopes.includes(ADMIN_SCOPES.ALL) && <PublicRoute path='/consults' component={() => <Consults />} />}
          {adminScopes.includes(ADMIN_SCOPES.ALL) && <PublicRoute path='/contract-task-templates' component={() => <ContractTaskTemplates />} />}
          {adminScopes.includes(ADMIN_SCOPES.ALL) && <PublicRoute path='/collaborations' component={() => <Contracts />} />}
          {adminScopes.includes(ADMIN_SCOPES.ALL) && <PublicRoute path='/tasks' component={() => <Contracts />} />}
          {adminScopes.includes(ADMIN_SCOPES.ALL) && <PublicRoute path='/analytics' component={() => <Analytics />} />}
          {adminScopes.includes(ADMIN_SCOPES.ALL) && <PublicRoute path='/chat' component={() => <Chat />} />}
          {adminScopes.includes(ADMIN_SCOPES.ALL) && <PublicRoute path='/growthscraper' component={() => <GrowthScraper />} />}
          {adminScopes.includes(ADMIN_SCOPES.ALL) && <PublicRoute path='/ml' component={() => <ML />} />}
          {adminScopes.includes(ADMIN_SCOPES.ALL) && <PublicRoute path='/creator-feedback' component={() => <CreatorFeedback />} />}
          {adminScopes.includes(ADMIN_SCOPES.ALL) && <PublicRoute path='/ml-training' component={() => <MlTraining />} />}
          {adminScopes.includes(ADMIN_SCOPES.ALL) && <PublicRoute path='/prompts' component={() => <Prompts />} />}
          {adminScopes.includes(ADMIN_SCOPES.ALL) && <PublicRoute path='/script-config' component={() => <ScriptConfig />} />}
          {adminScopes.includes(ADMIN_SCOPES.ALL) && <PublicRoute path='/lookbookorders' component={() => <LookbookOrders />} />}
          {adminScopes.includes(ADMIN_SCOPES.ALL) && <PublicRoute path='/playground' component={() => <Playground />} />}
        </Switch>
      </BrowserRouter>
    );
  }
}

const mapStateToProps = state => {
  const { users } = state;
  return { currentUser: users.currentUser };
};

export default connect(mapStateToProps, {
  logoutUser,
  syncCurrentUser
})(App);
